import styled from "styled-components";
import {useEffect, useRef, useState} from "react";
import {Observer} from "mobx-react";
import {XyiconFeature} from "../../../generated/api/base";
import type {IModel} from "../../../data/models/Model";
import type {Boundary} from "../../../data/models/Boundary";
import type {Space} from "../../../data/models/Space";
import type {DocumentModel} from "../../../data/models/DocumentModel";
import type {Xyicon} from "../../../data/models/Xyicon";
import type {Catalog} from "../../../data/models/Catalog";
import type {Markup} from "../../../data/models/Markup";
import {featureTitles} from "../../../data/state/AppStateConstants";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {IconButtonV5} from "../interaction/IconButtonV5";
import {ReactUtils} from "../../utils/ReactUtils";
import {BoundarySpaceMapSelectorV5} from "../spaceeditor/BoundarySpaceMapSelectorV5";
import InfoIcon from "../icons/circle-information.svg?react";
import PinIcon from "../icons/location-pin.svg?react";
import GridIcon from "../icons/list.svg?react";
import CardIcon from "../icons/grid.svg?react";
import BriefCaseIcon from "../icons/briefcase-alt.svg?react";
import OpenPortfolioIcon from "../icons/arrow-up-right-from-square.svg?react";
import {colorPalette} from "../styles/colorPalette";
import {ELLIPSIS, FlexCenterStyle, VerticalFlexStyle, baseDistance, fontSize, fontWeight, radius} from "../styles/styles";
import type {ModuleViewV5} from "../abstract/ModuleViewV5";
import type {TransformObj} from "../../../utils/dom/DomUtils";
import {DomUtils, VerticalAlignment, HorizontalAlignment} from "../../../utils/dom/DomUtils";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import type {OrgSearchResponseDto} from "../../../generated/api/search";
import type {Portfolio} from "../../../data/models/Portfolio";
import {openWorkspaceFromPortfolioModule} from "../modules/ActionBarUtils";
import {useAppStore} from "../../../StateManager";
import {InitialsV5} from "../widgets/InitialsV5";
import {GetPortfolioWideNavigationMenus} from "../../../Enums";
import {CardLayoutToolTipV5} from "./CardLayoutTooltipV5";
import {
	doesUserHavePermissionsToSpaceOfItem,
	extendLocalOrgSearchModel,
	getOrgSearchModelsByIds,
	onLocateClick,
	resolveLocalOrgSearchModel,
	type ScopeWideSearchType,
} from "./OrganizationWideSearchUtils";
import {selectWorkspaceViewTabForFeature} from "./ViewTabsCommon";

const isUnplotted = (item: IModel): boolean => {
	return item.ownFeature === XyiconFeature.Xyicon && (item as Xyicon).isUnplotted;
};

const isEmbedded = (item: IModel): boolean => {
	return item.ownFeature === XyiconFeature.Xyicon && (item as Xyicon).isEmbedded;
};

interface ISearchFeatureBlockV5Props {
	readonly scopeWideSearchType: ScopeWideSearchType;
	readonly feature: XyiconFeature;
	readonly featureItems: IModel[];
	readonly menuLabel: string;
	readonly queryString: string;
	readonly filteredLength: number;
	readonly itemsNumberWhenCollapsed: number;
	readonly isExpanded: boolean;
	readonly startExpandedMode: (feature: XyiconFeature) => void;
	readonly closeScopeWideSearch: () => void;
}

export const SearchFeatureBlockV5 = (props: ISearchFeatureBlockV5Props) => {
	const {
		scopeWideSearchType,
		feature,
		featureItems,
		menuLabel,
		queryString,
		filteredLength,
		itemsNumberWhenCollapsed,
		isExpanded,
		startExpandedMode,
		closeScopeWideSearch,
	} = props;
	const appState = useAppStore((state) => state.appState);
	const app = appState.app;
	const navigation = app.navigation;

	const _parentRefArray = useRef<HTMLDivElement[]>([]);
	const _cardLayoutTooltipRef = useRef<HTMLDivElement>(null);
	const _timeoutId = useRef<number>(null);
	const _block = useRef<HTMLDivElement>(null);

	const [detailedBoundary, setDetailedBoundary] = useState<Boundary | null>(null); // show spacemaps for this boundary (if not null)
	const [itemWithTooltipIndex, setItemWithTooltipIndex] = useState<number>(-1); // -1 means tooltip shouldn't be displayed
	const [tooltipTransform, setTooltipTransform] = useState<TransformObj | null>(null);

	const onMouseOverItem = (itemIndex: number, item: IModel) => {
		clearTimeout(_timeoutId.current);
		if (![XyiconFeature.Document, XyiconFeature.OrganizationDocument, XyiconFeature.PortfolioDocument].includes(item.ownFeature)) {
			_timeoutId.current = window.setTimeout(() => {
				if (itemIndex !== itemWithTooltipIndex) {
					setItemWithTooltipIndex(itemIndex);
				}
			}, 1000);
		}
	};

	const onMouseLeaveItem = () => {
		clearTimeout(_timeoutId.current);
		if (itemWithTooltipIndex !== -1) {
			setTooltipTransform(null);
			setItemWithTooltipIndex(-1);
		}
	};

	useEffect(() => {
		if (
			itemWithTooltipIndex !== -1 &&
			feature !== XyiconFeature.Markup &&
			_parentRefArray.current[itemWithTooltipIndex] &&
			_cardLayoutTooltipRef.current
		) {
			setTooltipTransform(
				DomUtils.getFixedFloatingElementPosition(
					_parentRefArray.current[itemWithTooltipIndex],
					_cardLayoutTooltipRef.current,
					VerticalAlignment.topOuter,
					HorizontalAlignment.center,
				),
			);
		}
	}, [itemWithTooltipIndex, feature]);

	const onShowBoundarySpaceMaps = (boundary: Boundary) => {
		setDetailedBoundary(boundary);
	};

	const onHideBoundarySpaceMaps = () => {
		setDetailedBoundary(null);
	};

	const getIcon = (featureItem: IModel) => {
		if ([XyiconFeature.Document, XyiconFeature.OrganizationDocument, XyiconFeature.PortfolioDocument, XyiconFeature.Space].includes(feature)) {
			const item = featureItem as DocumentModel | Space;

			return <img src={item.thumbnailFileURL} />;
		} else if (feature === XyiconFeature.Portfolio) {
			return (
				<InitialsV5
					name={(featureItem as Portfolio).name}
					backgroundColor={colorPalette.primary.c500Primary}
				/>
			);
		} else {
			const item = featureItem as Xyicon | Catalog | Boundary | Markup;

			return (
				<img
					src={item.thumbnail}
					style={{transform: (item as Xyicon).backgroundTransform || ""}}
				/>
			);
		}
	};

	const onPinClick = (item: Boundary | Xyicon | Markup) => {
		return onLocateClick(item, appState, onShowBoundarySpaceMaps);
	};

	const goToTableList = (item: IModel) => {
		return new Promise<void>((resolve) => {
			const nav = navigation.menus.find((menu) => menu.feature === feature).nav;
			let targetPortfolioId = "";

			app.onDetailsContainerClose(true);

			if (GetPortfolioWideNavigationMenus().includes(nav)) {
				const portfolioMaybe = appState.actions.getPortfolioById(item.portfolioId);

				if (portfolioMaybe) {
					targetPortfolioId = portfolioMaybe.id;
					navigation.goApp({
						mainNav: nav,
						portfolioRefId: portfolioMaybe.refId,
					});
				} else {
					console.warn(`Portfolio with id ${item.portfolioId} was not found.`);
				}
			} else {
				navigation.goApp({
					mainNav: nav,
				});
			}
			//TODO: try mobx when
			const focusItem = async () => {
				const moduleView = app.moduleViews[feature] as ModuleViewV5 | undefined;
				const isPortfolioSwitchRequestInProgress = useAppStore.getState().isPortfolioSwitchRequestInProgress;
				const isTargetPortfolioReady = !targetPortfolioId || appState.activePortfolioId === targetPortfolioId;
				const isFeatureSelected = appState.selectedFeature === feature;
				const isLoading = !moduleView || !moduleView.isMounted || moduleView.isLoadingList;

				if (!isFeatureSelected || !isTargetPortfolioReady || isPortfolioSwitchRequestInProgress || isLoading) {
					setTimeout(() => {
						focusItem();
					}, 20);
					return;
				}
				await moduleView.focusItem(item);
				// This should be after the navigation, otherwise you can see the previous module for a split second
				closeScopeWideSearch();
				resolve();
			};

			focusItem();
		});
	};

	const getGoToTableListButton = (item: IModel, listMode: "grid" | "card") => {
		return (
			<IconButtonV5
				IconComponent={listMode === "grid" ? GridIcon : CardIcon}
				title={listMode === "grid" ? "Show in List" : "Show in Spaces"}
				onClick={async (event) => {
					event.stopPropagation(); // Don't select details

					selectWorkspaceViewTabForFeature(appState, item.ownFeature, true);
					await goToTableList(item);
				}}
			/>
		);
	};

	const getPinButtonMaybe = (item: IModel) => {
		const userHasViewPermissionToAtLeastOneSpaceOfItem = doesUserHavePermissionsToSpaceOfItem(item, appState);

		if (userHasViewPermissionToAtLeastOneSpaceOfItem) {
			return (
				<IconButtonV5
					IconComponent={PinIcon}
					title="Locate"
					onClick={(event: React.MouseEvent<HTMLDivElement>) => {
						event.stopPropagation(); // don't open the details panel when pin is clicked
						onPinClick(item as Xyicon | Boundary);
					}}
				/>
			);
		}

		return <></>;
	};

	const renderCustomRow = (data: {key: string; value: string}) => {
		let html = "";

		if (data.value !== "") {
			html = data.key + (data.key !== "" ? ": " : "") + StringUtils.regexHighlight(data.value.toString(), queryString);
		}

		return (
			<div
				className="field"
				dangerouslySetInnerHTML={{
					__html: html,
				}}
			/>
		);
	};

	const onExpand = () => {
		startExpandedMode(feature);
	};

	const inlineStyle: React.CSSProperties = {
		transform: `translate(${_parentRefArray.current[itemWithTooltipIndex]?.getBoundingClientRect().x}px, ${tooltipTransform?.y + 10}px)`,
		maxWidth: _parentRefArray.current[0]?.offsetWidth,
		visibility: tooltipTransform ? "visible" : "hidden",
	};

	return (
		<Observer>
			{() => {
				return (
					<SearchFeatureBlockStyled
						className={`SearchFeatureBlock ${featureTitles[feature]}`}
						data-featureid={feature}
						ref={_block}
					>
						{!isExpanded && <h3 data-featureid={feature}>{menuLabel}</h3>}
						<div className="items">
							{featureItems.map((item: IModel, index) => {
								const rows = appState.actions.getRowsForCardLayout(queryString, item);

								const itemInDetailsContainer = appState.app.overlayedDetailedItems[0];

								return (
									<div
										ref={(parentRef) => {
											_parentRefArray.current[index] = parentRef;
										}}
										className={ReactUtils.cls("moduleItem", {
											focused:
												itemInDetailsContainer?.model.id === item?.id ||
												(item as DocumentModel)?.doesBelongToObject?.(itemInDetailsContainer?.model.id),
										})}
										key={item?.id ?? index}
										onMouseOver={() => onMouseOverItem(index, item)}
										onMouseLeave={onMouseLeaveItem}
									>
										<div className="icon">{getIcon(item)}</div>
										<div className="fields">
											<div
												className={ReactUtils.cls("featureId", {
													unplotted: isUnplotted(item),
													embedded: isEmbedded(item),
												})}
											>
												{renderCustomRow(rows[0])}
											</div>
											{renderCustomRow(rows[1])}
											{scopeWideSearchType === "Organization" && (item.data as OrgSearchResponseDto).portfolioName ? (
												<div
													className="field"
													style={{fontWeight: `${fontWeight.bold}`, color: `${colorPalette.gray.c950}`}}
												>
													<BriefCaseIcon
														className="briefCase"
														width="12"
														height="12"
													/>
													<span style={{marginLeft: "14px"}}>{(item.data as OrgSearchResponseDto).portfolioName ?? ""}</span>
												</div>
											) : (
												renderCustomRow(rows[2])
											)}
											{detailedBoundary === item && (
												<BoundarySpaceMapSelectorV5
													item={item as Boundary}
													onClose={onHideBoundarySpaceMaps}
													closeScopeWideSearch={closeScopeWideSearch}
												/>
											)}
										</div>
										<div className="hoverButtons hbox">
											<IconButtonV5
												IconComponent={InfoIcon}
												title="Details"
												onClick={async () => {
													let itemToOpen = item;
													let closeEverythingButDocuments = false;
													const isDocument = [XyiconFeature.OrganizationDocument, XyiconFeature.PortfolioDocument, XyiconFeature.Document].includes(
														feature,
													);

													if (isDocument) {
														itemToOpen = (item as DocumentModel).parentElementMaybe;

														if (!itemToOpen && (item as DocumentModel).documentMap?.length > 0) {
															const documentParents = await getOrgSearchModelsByIds(
																appState,
																(item as DocumentModel).documentMap.map((d) => d.objectID),
															);

															if (documentParents.length === 1) {
																itemToOpen = documentParents[0];
															}
														}

														if (itemToOpen) {
															closeEverythingButDocuments = true;
														}
													}

													if (itemToOpen) {
														// For documents, we already do this within getOrgSearchModelsByIds
														if (!isDocument) {
															itemToOpen = resolveLocalOrgSearchModel(appState, itemToOpen);
															if (itemToOpen.portfolioId && itemToOpen.portfolioId !== appState.activePortfolioId) {
																itemToOpen = await extendLocalOrgSearchModel(appState, itemToOpen);
															}
														}
														if (closeEverythingButDocuments) {
															appState.isDetailsContentBeingOpenByDocumentOfItem = true;
														}
														app.onDetailsClick(itemToOpen, true);
													}
												}}
											/>
											{feature === XyiconFeature.Portfolio && (
												<IconButtonV5
													IconComponent={OpenPortfolioIcon}
													title="Open"
													onClick={() => {
														openWorkspaceFromPortfolioModule(appState, item.id);
													}}
												/>
											)}
											{getPinButtonMaybe(item)}
											{![XyiconFeature.Document, XyiconFeature.OrganizationDocument, XyiconFeature.PortfolioDocument, XyiconFeature.Markup].includes(
												feature,
											) && getGoToTableListButton(item, feature === XyiconFeature.Space ? "card" : "grid")}
										</div>
										{itemWithTooltipIndex === index &&
											[XyiconFeature.Space, XyiconFeature.XyiconCatalog, XyiconFeature.Xyicon, XyiconFeature.Boundary].includes(item.ownFeature) && (
												<DomPortal destination={app.modalContainer}>
													<CardLayoutToolTipV5
														item={item as Xyicon}
														divRef={_cardLayoutTooltipRef}
														style={inlineStyle}
														className={ReactUtils.cls({left: tooltipTransform?.horizontal === HorizontalAlignment.left})}
														queryString={queryString}
													/>
												</DomPortal>
											)}
									</div>
								);
							})}
						</div>
						{
							<InfoBox>
								{!isExpanded && (
									<>
										<p>
											Showing
											<span className="highLighted">{` ${filteredLength <= itemsNumberWhenCollapsed ? filteredLength : itemsNumberWhenCollapsed}`}</span>
											{filteredLength >= itemsNumberWhenCollapsed && (
												<span>
													{" "}
													of <span className="highLighted">{filteredLength}</span>
												</span>
											)}
										</p>
										{filteredLength > itemsNumberWhenCollapsed && (
											<LinkButton
												onClick={onExpand}
												title="Show More"
											>
												Show More
											</LinkButton>
										)}
									</>
								)}
							</InfoBox>
						}
					</SearchFeatureBlockStyled>
				);
			}}
		</Observer>
	);
};

export const SearchFeatureBlockStyled = styled.div`
	${VerticalFlexStyle};
	gap: ${baseDistance.sm};
	border-radius: ${radius.md};
	border: solid 1px transparent;
	padding: ${baseDistance.sm};

	&:hover {
		border-color: ${colorPalette.gray.c300};
	}

	h3 {
		font-size: 24px;
		font-weight: ${fontWeight.bold};
	}

	.items {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));

		.moduleItem {
			${FlexCenterStyle};
			gap: ${baseDistance.sm};
			position: relative;
			padding: ${baseDistance.sm};
			border-radius: ${radius.sm};
			cursor: pointer;

			&.focused {
				background-color: ${colorPalette.primary.c200Light};

				.hoverButtons {
					background: none;

					.IconButton {
						background-color: ${colorPalette.primary.c200Light};
						color: ${colorPalette.primary.c500Primary};
					}
				}
			}

			&:hover,
			&.focused {
				background-color: ${colorPalette.gray.c200Light};

				.hoverButtons {
					display: flex;
					align-items: center;

					.IconButton {
						box-sizing: border-box;
						border: solid 2px transparent;
						transition: ease-in-out 0.2s border-color;

						&:hover {
							border-color: ${colorPalette.gray.c950};
						}
					}
				}

				&.focused {
					background-color: ${colorPalette.primary.c200Light};

					.hoverButtons {
						.IconButton {
							&:hover {
								border-color: ${colorPalette.primary.c500Primary};
							}
						}
					}
				}
			}

			.icon {
				width: 40px;
				min-width: 40px;
				height: 40px;

				img {
					width: 100%;
				}
			}

			.Initials {
				width: 40px;
				height: 40px;
				font-size: ${fontSize.lg};
				font-weight: ${fontWeight.bold};
				color: ${colorPalette.white};
				top: auto;
				right: auto;
				position: relative;
				display: flex;
				justify-content: center;
				align-items: center;
			}

			.fields {
				flex: 1;
				display: grid;
				gap: 2px;
				color: ${colorPalette.gray.c700Dark};

				.featureId {
					${ELLIPSIS}

					.field {
						display: inline;
						font-size: ${fontSize.md};
						color: ${colorPalette.gray.c950};
					}

					&.unplotted {
						&::after {
							content: "(Unplotted)";
							font-weight: 200;
							margin-left: ${baseDistance.xs};
							font-size: ${fontSize.md};
						}
					}

					&.embedded {
						&::after {
							content: "(Embedded)";
							font-weight: 200;
							margin-left: ${baseDistance.xs};
							font-size: ${fontSize.md};
						}
					}
				}

				.field {
					font-size: ${fontSize.sm};
					${ELLIPSIS}

					.briefCase {
						position: absolute;
						min-width: 12px;
					}
				}
			}

			.hoverButtons {
				background: ${colorPalette.gray.c200Light};
				display: none;
			}
		}
	}

	&.Document {
		.moduleItem:hover {
			border-color: ${colorPalette.gray.c200Light};
			cursor: default;
		}
	}
`;

const InfoBox = styled.div`
	${FlexCenterStyle};
	justify-content: flex-end;
	gap: ${baseDistance.md};

	p {
		font-size: ${fontSize.md};
	}
`;

const LinkButton = styled.div`
	font-size: ${fontSize.md};
	line-height: ${fontSize.lg};
	color: ${colorPalette.primary.c500Primary};
	font-weight: ${fontWeight.bold};
	text-decoration: underline;
	cursor: pointer;
`;
